import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type StaffPermission =
  | 'products.manage'
  | 'vendors.manage'
  | 'vendors.approve'
  | 'orders.manage'
  | 'orders.view'
  | 'cms.manage'
  | 'users.view'
  | 'analytics.view'
  | 'roadblockers.manage'
  | 'payments.view';

export interface Admin {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'super_admin' | 'staff';
  /** Empty for admin/super_admin (they implicitly have everything). */
  permissions: string[];
  avatar?: string;
}

/** True if the user can access a feature gated by `perm`. Admin/super_admin
 *  implicitly hold every permission. */
export function adminCan(admin: Admin | null, perm: StaffPermission): boolean {
  if (!admin) return false;
  if (admin.role === 'admin' || admin.role === 'super_admin') return true;
  return admin.permissions?.includes(perm) ?? false;
}

interface AdminState {
  admin: Admin | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

interface AdminActions {
  setAdmin: (admin: Admin) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
}

export const useAdminStore = create<AdminState & AdminActions>()(
  persist(
    (set) => ({
      admin: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setAdmin: (admin) => set({ admin }),

      setTokens: (accessToken, refreshToken) => {
        localStorage.setItem('admin_token', accessToken);
        localStorage.setItem('admin_refresh_token', refreshToken);
        set({ accessToken, refreshToken, isAuthenticated: true });
      },

      logout: () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        set({ admin: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },
    }),
    {
      name: 'admin-auth',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
